import { useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";

const touch = () => window.matchMedia?.("(pointer: coarse)").matches || window.innerWidth < 640;

function Body({ icons, value, onPick, q, setQ, autoFocus, extra, testid }) {
  const t = q.trim().toLowerCase();
  const shown = Object.entries(icons).filter(([k, [, label]]) => !t || k.includes(t) || label.toLowerCase().includes(t));
  return (
    <>
      {extra}
      <div className="mt-3 relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search icons" data-testid={`${testid.replace(/-picker$/, "")}-search`}
          className="w-full h-10 pl-9 pr-3 rounded-full bg-muted/60 text-base sm:text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      </div>
      <div className="mt-3 grid grid-cols-6 sm:grid-cols-7 gap-1.5 max-h-[45dvh] sm:max-h-72 overflow-y-auto pr-1 -mr-1 overscroll-contain touch-pan-y">
        {shown.map(([k, [I, label]]) => (
          <button key={k} type="button" title={label} aria-label={label} onClick={() => onPick(k)} data-testid={`${testid.replace(/-picker$/, "")}-${k}`}
            className={`h-11 sm:h-10 rounded-xl inline-flex items-center justify-center transition-colors active:scale-95 ${value === k ? "bg-foreground text-background" : "bg-muted/60 hover:bg-muted"}`}>
            <I weight="duotone" className="h-5 w-5" />
          </button>
        ))}
        {!shown.length && <p className="col-span-full py-6 text-center text-sm text-muted-foreground" data-testid={`${testid.replace(/-picker$/, "")}-empty`}>No icons match "{q}".</p>}
      </div>
    </>
  );
}

export default function IconChooser({ title, subtitle, icons, value, onPick, extra, testid, children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobile, setMobile] = useState(false);
  const change = (o) => { if (o) setMobile(touch()); else setQ(""); setOpen(o); };
  const body = <Body icons={icons} value={value} onPick={onPick} q={q} setQ={setQ} autoFocus={!mobile} extra={extra} testid={testid} />;
  return (
    <>
      <Popover open={open && !mobile} onOpenChange={change}>
        <PopoverTrigger asChild onClick={(e) => { e.preventDefault(); change(!open); }}>{children}</PopoverTrigger>
        <PopoverContent align="start" className="w-80 rounded-3xl border-0 p-4 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.3)]" data-testid={testid}>
          <p className="text-sm font-semibold tracking-tight">{title}</p>
          <p className="text-[11px] text-muted-foreground">{subtitle}</p>
          {body}
        </PopoverContent>
      </Popover>
      <Sheet open={open && mobile} onOpenChange={change}>
        <SheetContent side="bottom" guard={false} className="rounded-t-[28px] border-0 px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-6" data-testid={testid}>
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg tracking-tight">{title}</SheetTitle>
            <SheetDescription className="text-xs">{subtitle}</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    </>
  );
}
